import { defineStore } from 'pinia';

export interface StoryContent {
  [key: string]: any;
}

interface StoryblokState {
  stories: Record<string, StoryContent | null>;
  isLoading: boolean;
}

export const useStoryblokStore = defineStore('storyblokStore', {
  state: (): StoryblokState => ({
    stories: {}, // Stores all stories by slug
    isLoading: false, // Loading state
  }),

  getters: {
    // Getter to retrieve a story by name
    getStoryByName:
      (state) =>
      (name: string): StoryContent | null => {
        return state.stories[name] || null;
      },
  },

  actions: {
    // Cache-aware function to set story data
    setStory(name: string, data: StoryContent) {
      this.stories[name] = data;
    },

    // Optimized function to fetch multiple stories with caching and batching
    async fetchMultipleStories(storyNames: string[]) {
      const storiesToFetch = storyNames.filter((storyName) => !this.stories[storyName]);

      if (storiesToFetch.length === 0) {
        return;
      }

      this.isLoading = true;

      try {
        const promises = storiesToFetch?.map((storyName) => this.fetchStory(storyName));
        await Promise.all(promises);
      } catch (error) {
        console.error('Error fetching stories:', error);
      } finally {
        this.isLoading = false;
      }
    },

    // Fetch a single story with caching
    async fetchStory(storyName: string) {
      if (this.stories[storyName]) {
        return;
      }

      try {
        const config = useRuntimeConfig();
        const story = await useAsyncStoryblok(storyName, {
          version: config.public.storyblokVersion as 'published' | 'draft',
        });

        if (story?.value) {
          this.setStory(storyName, story.value.content);
        }
      } catch (error) {
        console.error(`Error fetching story "${storyName}":`, error);
      }
    },
  },
});
